import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

const VIEW = 320;   // on-screen crop viewport (px)
const OUT = 512;    // output size — square JPEG, auto-compressed

/** 1:1 crop + compress dialog. Drag to reposition, slider to zoom. */
export const ImageCropDialog = ({ file, onDone, onCancel }) => {
  const [url, setUrl] = useState('');
  const [img, setImg] = useState(null); // { w, h, el }
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // image top-left within viewport
  const drag = useRef(null);

  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    const i = new Image();
    i.onload = () => {
      const cover = VIEW / Math.min(i.naturalWidth, i.naturalHeight);
      setImg({ w: i.naturalWidth, h: i.naturalHeight, el: i });
      setZoom(1);
      setPos({ x: (VIEW - i.naturalWidth * cover) / 2, y: (VIEW - i.naturalHeight * cover) / 2 });
    };
    i.src = u;
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const cover = img ? VIEW / Math.min(img.w, img.h) : 1;
  const scale = cover * zoom;

  const clampPos = useCallback((p, z) => {
    if (!img) return p;
    const s = cover * z;
    return {
      x: Math.min(0, Math.max(VIEW - img.w * s, p.x)),
      y: Math.min(0, Math.max(VIEW - img.h * s, p.y)),
    };
  }, [img, cover]);

  const onZoom = (z) => {
    setPos((p) => {
      const s1 = cover * zoom;
      const s2 = cover * z;
      return clampPos({
        x: VIEW / 2 - ((VIEW / 2 - p.x) / s1) * s2,
        y: VIEW / 2 - ((VIEW / 2 - p.y) / s1) * s2,
      }, z);
    });
    setZoom(z);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPos(clampPos({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) }, zoom));
  };
  const onPointerUp = () => { drag.current = null; };

  const confirm = async () => {
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    const sx = -pos.x / scale;
    const sy = -pos.y / scale;
    const sw = VIEW / scale;
    ctx.drawImage(img.el, sx, sy, sw, sw, 0, 0, OUT, OUT);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob) { onCancel(); return; }
    const name = `${(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
    onDone(new File([blob], name, { type: 'image/jpeg' }));
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Crop your photo</DialogTitle>
          <DialogDescription>Drag to reposition and zoom to fit — the photo is saved as a square and compressed automatically.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <div
            className="relative overflow-hidden rounded-2xl border border-border bg-secondary touch-none cursor-move select-none"
            style={{ width: VIEW, height: VIEW }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            data-testid="avatar-crop-viewport"
          >
            {url && img && (
              <img
                src={url}
                alt="Crop preview"
                draggable={false}
                className="absolute max-w-none pointer-events-none"
                style={{ width: img.w * scale, height: img.h * scale, left: pos.x, top: pos.y }}
              />
            )}
            <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-foreground/10 pointer-events-none" />
          </div>
        </div>
        <div className="flex items-center gap-3 px-1">
          <span className="text-[10px] font-grotesk uppercase tracking-wider text-muted-foreground">Zoom</span>
          <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={([z]) => onZoom(z)} className="flex-1" data-testid="avatar-crop-zoom" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel} data-testid="avatar-crop-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={!img} className="rounded-full px-6" data-testid="avatar-crop-confirm">Use photo</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook: returns a `transform` you can hand to <FileUpload transform={...}> and a
 * `dialog` element to render. Every picked image runs through the 1:1 crop +
 * compression dialog before uploading; resolving null cancels the upload.
 */
export function useAvatarCrop() {
  const [pending, setPending] = useState(null); // { file, resolve }
  const transform = useCallback((file) => new Promise((resolve) => {
    if (!file?.type?.startsWith('image/')) { resolve(file); return; }
    setPending({ file, resolve });
  }), []);
  const dialog = pending ? (
    <ImageCropDialog
      file={pending.file}
      onDone={(cropped) => { pending.resolve(cropped); setPending(null); }}
      onCancel={() => { pending.resolve(null); setPending(null); }}
    />
  ) : null;
  return { transform, dialog };
}

export default ImageCropDialog;
